// Bestellung – Belegart 'purchase_order' (BE-). Der Standard-Vertragssatz kommt aus der Belegfabrik; dazu Freigabe
// über der Grenze, der Stand der Lieferung je Zeile und der Wareneingang zur Bestellung (über das Modul Bestand).
//
//   Entwurf → (Zur Freigabe ⇄ Zurückgegeben → Freigegeben) → Bestellt → Teilweise geliefert → Geliefert → Abgeschlossen
//
// „Festschreiben“ heißt hier „Als bestellt markieren“: Das System sendet nichts – der Einkauf bestellt per E-Mail,
// Telefon oder Portal und hält den Weg fest (Kopf-Zusatzfeld `sentVia`). Mengen der Zeilen stehen in Tausendstel
// des Gebindes der Zeile (Bestelleinheit), Preise in Cent netto je Preiseinheit.
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { defineDocumentContracts, zDocumentView } from '@shared/document'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { SEND_CHANNELS } from '../lib/labels'
import { ORDER_TYPE, zDay, zNote, zPerson, zReason, zSupplierRef, zUnitQtyOrZero } from './common'

const PERMISSIONS = {
  read: 'purchasing.order.read',
  write: 'purchasing.order.write',
  post: 'purchasing.order.write',
  reverse: 'purchasing.order.write',
} as const

export const order = defineDocumentContracts({
  module: 'purchasing',
  entity: 'order',
  documentType: ORDER_TYPE,
  title: 'Bestellung',
  titlePlural: 'Bestellungen',
  permissions: PERMISSIONS,
  keywords: ['Bestellung', 'Lieferantenbestellung', 'bestellen', 'Einkauf', 'Lieferant', 'Bestellnummer', 'BE'],
})

export const orderGet = order.get
export const orderList = order.list
export const orderSaveDraft = order.saveDraft
/** „Als bestellt markieren“ – über der Freigabegrenze der Person geht die Bestellung stattdessen zur Freigabe */
export const orderPost = order.post
/** Bestellung abbrechen (solange nichts geliefert ist) */
export const orderReverse = order.reverse
/** „Rest stornieren“ (bestellt/teilweise geliefert → abgeschlossen) und „Abschließen“ (geliefert → abgeschlossen) */
export const orderTransition = order.transition
export const orderPrint = order.print
export const orderDiscard = order.discard

export const orderApprove = defineContract({
  name: 'purchasing.order.approve',
  title: 'Bestellung freigeben',
  description: 'Gibt eine Bestellung frei, die über der Grenze der bestellenden Person liegt. Danach kann sie bestellt werden.',
  llm: {
    description:
      'Gibt eine Bestellung im Zustand „Zur Freigabe“ frei. Erlaubt nur einer zweiten Person (nie, wer die Bestellung ' +
      'angelegt oder eingereicht hat), deren Freigabegrenze den Warenwert deckt. Die Bestellung ist danach ' +
      '„Freigegeben“ – bestellt ist sie erst, wenn der Einkauf sie mit purchasing.order.post als bestellt markiert. ' +
      'Zum Zurückgeben mit Grund: purchasing.order.reject.',
    keywords: ['Bestellung freigeben', 'Freigabe', 'genehmigen'],
  },
  input: z.strictObject({ id: zId }),
  output: zDocumentView,
  permission: 'purchasing.order.approve',
  risk: 'financial',
  scope: 'record',
  fieldPermissions: { 'lines.*.costCents': PERMISSION_COSTS_READ },
})

export const orderReject = defineContract({
  name: 'purchasing.order.reject',
  title: 'Bestellung zurückgeben',
  description: 'Gibt eine Bestellung, die auf Freigabe wartet, mit Grund an den Einkauf zurück.',
  llm: {
    description: 'Lehnt die Freigabe einer Bestellung ab und gibt sie mit Grund zurück: Sie wird wieder bearbeitbar, bestellt wird nichts. Nur im Zustand „Zur Freigabe“.',
    keywords: ['Bestellung ablehnen', 'zurückgeben', 'Freigabe ablehnen'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: zDocumentView,
  permission: 'purchasing.order.approve',
  risk: 'write',
  scope: 'record',
  fieldPermissions: { 'lines.*.costCents': PERMISSION_COSTS_READ },
})

export const orderWithdraw = defineContract({
  name: 'purchasing.order.withdraw',
  title: 'Bestellung zurück in Bearbeitung nehmen',
  description: 'Holt eine wartende oder freigegebene, noch nicht bestellte Bestellung zurück in die Bearbeitung.',
  llm: {
    description:
      'Nimmt eine Bestellung im Zustand „Zur Freigabe“ oder „Freigegeben“ zurück in die Bearbeitung (Einkauf), etwa ' +
      'weil sich Mengen ändern. Eine erteilte Freigabe verfällt damit – nach der Änderung wird neu eingereicht.',
    keywords: ['Freigabe zurückziehen', 'Bestellung ändern', 'zurück in Bearbeitung'],
  },
  input: z.strictObject({ id: zId, reason: z.string().trim().max(500).nullish() }),
  output: zDocumentView,
  permission: PERMISSIONS.write,
  risk: 'write',
  scope: 'record',
  fieldPermissions: { 'lines.*.costCents': PERMISSION_COSTS_READ },
})

export const zOrderLineProgress = z.object({
  lineId: zId,
  pos: z.int(),
  itemId: zId.nullable(),
  sku: z.string().nullable(),
  description: z.string().nullable(),
  /** Gebinde der Zeile (item_unit.id) und seine Kurzform: „Pal“ */
  unitId: zId.nullable(),
  unit: z.string().nullable(),
  /** Lagergebinde des Artikels – darin zählt das Lager („Ka“) */
  stockUnit: z.object({ id: zId, short: z.string(), baseQty: z.int() }).nullable(),
  /** Basismenge je Gebinde der Zeile in Tausendstel */
  unitBaseQty: z.int(),
  /** alle drei im Gebinde der Zeile, Tausendstel (1 Palette = 1000) */
  orderedQty: z.int(),
  receivedQty: z.int(),
  openQty: z.int(),
  /** dieselben Mengen im Lagergebinde als Text: „238 von 240 Ka“ */
  progressText: z.string(),
  /** aus Wareneingängen gemeldet: fehlend bzw. beschädigt, im Gebinde der Zeile */
  shortQty: z.int(),
  damagedQty: z.int(),
  /** Grundlage aus dem Bestellvorschlag, falls die Zeile daraus entstand */
  basisText: z.string().nullable(),
})

export const orderDetail = defineContract({
  name: 'purchasing.order.detail',
  title: 'Stand einer Bestellung anzeigen',
  description: 'Freigabe, Bestellweg, gelieferte und offene Mengen je Zeile, Wareneingänge und Rechnungen einer Bestellung.',
  llm: {
    description:
      'Liefert zu einer Bestellung, was der Beleg selbst nicht zeigt: Stand der Freigabe (wer eingereicht hat, ob der ' +
      'Aufrufer freigeben darf), wie und wann bestellt wurde, je Zeile bestellte, gelieferte und offene Menge (im ' +
      'Gebinde der Zeile, Tausendstel), gemeldete Fehlmengen und Beschädigungen, die Wareneingänge und ' +
      'Eingangsrechnungen dazu und – wenn sie aus einem Bestellvorschlag stammt – die Grundlage je Zeile. Verwenden ' +
      'für „Was ist von BE-… noch offen?“. Den Beleg selbst liefert purchasing.order.get.',
    keywords: ['offene Menge', 'Lieferstand', 'Was fehlt noch', 'Rückstand', 'Freigabe', 'Bestellweg'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    id: zId,
    number: z.string().nullable(),
    status: z.string(),
    statusLabel: z.string(),
    supplier: zSupplierRef.nullable(),
    locationId: zId,
    locationCode: z.string(),
    deliveryDate: z.string().nullable(),
    /** Warenwert netto ohne Pfand – Grundlage der Freigabegrenze; nur mit „Einstandspreise sehen“ */
    valueCents: z.int().optional(),
    approval: z.object({
      /** die Bestellung wartet auf Freigabe */
      pending: z.boolean(),
      /** würde der Aufrufer sie jetzt als bestellt markieren, ginge sie zur Freigabe */
      required: z.boolean(),
      /** Freigabegrenze des Aufrufers in Cent; null = unbegrenzt */
      limitCents: z.int().nullable().optional(),
      submittedBy: zPerson.nullable(),
      submittedAt: z.date().nullable(),
      approvedBy: zPerson.nullable(),
      approvedAt: z.date().nullable(),
      returnReason: z.string().nullable(),
      canApprove: z.boolean(),
      /** warum der Aufrufer nicht freigeben darf (Vier-Augen, Grenze) – nur wenn er das Recht grundsätzlich hat */
      approveHint: z.string().nullable(),
      canWithdraw: z.boolean(),
    }),
    sent: z.object({ via: z.enum(SEND_CHANNELS).nullable(), viaLabel: z.string().nullable(), at: z.string().nullable(), byName: z.string().nullable() }),
    /** vom Lieferanten hinterlegter Bestellweg (Vorschlag) */
    suggestedChannel: z.enum(SEND_CHANNELS).nullable(),
    lines: z.array(zOrderLineProgress),
    /** es gibt offene Mengen und der Aufrufer darf am Standort Wareneingänge erfassen */
    canReceive: z.boolean(),
    receipts: z.array(z.object({ id: zId, number: z.string().nullable(), status: z.string(), statusLabel: z.string(), documentDate: z.string(), externalRef: z.string().nullable() })),
    invoices: z.array(z.object({ id: zId, number: z.string().nullable(), status: z.string(), statusLabel: z.string(), documentDate: z.string(), externalRef: z.string().nullable() })),
    /** unter dem Mindestbestellwert des Lieferanten (nur mit „Einstandspreise sehen“) */
    belowMinimum: z.object({ minOrderValueCents: z.int(), missingCents: z.int() }).nullable().optional(),
  }),
  permission: PERMISSIONS.read,
  risk: 'read',
  scope: 'record',
  fieldPermissions: { valueCents: PERMISSION_COSTS_READ, 'approval.limitCents': PERMISSION_COSTS_READ, belowMinimum: PERMISSION_COSTS_READ },
})

export const RECEIVE_OUTCOMES = ['draft', 'posted', 'pending'] as const

export const orderReceive = defineContract({
  name: 'purchasing.order.receive',
  title: 'Wareneingang zur Bestellung erfassen',
  description: 'Legt aus einer oder mehreren Bestellungen eines Lieferanten einen Wareneingang an – auf Wunsch gleich gebucht.',
  llm: {
    description:
      'Legt den Wareneingang zu einer oder mehreren Bestellungen DESSELBEN Lieferanten am selben Standort an (über ' +
      'das Modul Bestand: inventory.receipt.derive). Ohne `lines` wird je Zeile die offene Restmenge vorgeschlagen und ' +
      'der Wareneingang bleibt ein Entwurf, den das Lager am Beleg ergänzt (Abweichungen und Pflichtangaben). Mit `lines` ' +
      'und `post: true` wird er in einem Schritt gebucht: je Bestellzeile die angekommene Menge (Tausendstel des ' +
      'Gebindes der Bestellzeile oder des mit `unitId` genannten; 0 = nichts angekommen), ' +
      'Pflichtangaben, wo der Artikel sie verlangt, `shortQty` (fehlt laut Lieferschein) und `damagedQty` ' +
      '(beschädigt, in der Menge enthalten) im selben Gebinde. Mehr als offen ist plus Toleranz lässt ' +
      'sich nicht buchen. Über der Wertgrenze der Rolle wartet der Wareneingang auf Freigabe (`outcome: pending`). ' +
      'Für Lieferungen ohne Bestellung: inventory.receipt.receive.',
    keywords: ['Wareneingang', 'Lieferung annehmen', 'Bestellung einbuchen', 'angekommen', 'Lieferschein', 'Fehlmenge', 'Bruch'],
  },
  input: z.strictObject({
    orderIds: z.array(zId).min(1, { error: 'Bitte wählen Sie mindestens eine Bestellung.' }).max(20),
    /** Lieferschein-Nummer des Lieferanten */
    externalRef: z.string().trim().max(120).nullish(),
    documentDate: zDay.optional(),
    lines: z
      .array(
        z.strictObject({
          /** Zeile der Bestellung – mehrere Einträge je Zeile sind erlaubt (je Charge einer) */
          lineId: zId,
          /** Gebinde, in dem gezählt wurde (item_unit.id); ohne Angabe das der Bestellzeile */
          unitId: zId.nullish(),
          /** angekommene Menge in diesem Gebinde (Tausendstel) */
          qty: zUnitQtyOrZero,
          lotNo: z.string().trim().max(80).nullish(),
          bestBefore: zDay.nullish(),
          /** Seriennummern der gelieferten Stücke – eine je Stück, nur bei Artikeln mit Seriennummernpflicht */
          serialNos: z.array(z.string().trim().min(1).max(80)).max(500).nullish(),
          shortQty: zUnitQtyOrZero.optional(),
          damagedQty: zUnitQtyOrZero.optional(),
          note: zNote,
        }),
      )
      .max(500)
      .optional(),
    /** true: gleich buchen (Scan-Ablauf am Telefon); sonst bleibt der Wareneingang ein Entwurf */
    post: z.boolean().default(false),
  }),
  output: z.object({
    receiptId: zId,
    number: z.string().nullable(),
    outcome: z.enum(RECEIVE_OUTCOMES),
    message: z.string(),
  }),
  // das Recht, am Standort Wareneingänge zu erfassen; die Bestellung selbst muss die Person lesen dürfen
  permission: 'inventory.receipt.create',
  risk: 'financial',
  scope: 'record',
  limits: { maxItems: 500, itemCount: (input) => input.lines?.length ?? input.orderIds.length },
})

export const zExpectedOrder = z.object({
  id: zId,
  number: z.string().nullable(),
  status: z.string(),
  statusLabel: z.string(),
  supplier: zSupplierRef,
  locationId: zId,
  locationCode: z.string(),
  documentDate: z.string(),
  /** Wunschtermin; null = ohne Termin */
  deliveryDate: z.string().nullable(),
  /** Tage bis zum Termin (negativ = überfällig); null ohne Termin */
  dueInDays: z.int().nullable(),
  lineCount: z.int(),
  /** Zeilen mit offener Menge */
  openLineCount: z.int(),
  /** „6 Pal Hell · 2 Pal Edelstoff · 10 KEG“ */
  summary: z.string(),
  netCents: z.int().optional(),
})

export const orderExpected = defineContract({
  name: 'purchasing.order.expected',
  title: 'Erwartete Lieferungen anzeigen',
  description: 'Bestellte, noch nicht vollständig gelieferte Bestellungen mit Termin – für Lager und Einkauf.',
  llm: {
    description:
      'Listet bestellte Bestellungen mit offenen Mengen an den Standorten, die der Nutzer sehen darf, sortiert nach ' +
      'Wunschtermin: Lieferant, Termin, überfällig seit, offene Zeilen. Filter: Standort, Lieferant, Zeitraum des ' +
      'Termins. Verwenden für „Was kommt heute?“ oder „Was ist bei diesem Lieferanten noch offen?“.',
    keywords: ['erwartete Lieferung', 'Was kommt heute', 'offene Bestellungen', 'Rückstand', 'überfällig', 'Anlieferung'],
  },
  input: z.strictObject({
    locationId: zId.optional(),
    supplierId: zId.optional(),
    from: zDay.optional(),
    to: zDay.optional(),
    limit: z.int().min(1).max(200).default(100),
  }),
  output: z.object({ rows: z.array(zExpectedOrder), today: z.string() }),
  permission: PERMISSIONS.read,
  risk: 'read',
  scope: 'any',
  fieldPermissions: { 'rows.*.netCents': PERMISSION_COSTS_READ },
})
